/** @jsx jsx */
import { memo, useCallback, useState, useMemo, useRef } from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { cssMap } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { BoardCardActions_column$key } from '@atlassian/jira-relay/src/__generated__/BoardCardActions_column.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ModalContextProvider } from '@atlassian/jira-modal-context-provider/src/ModalContextProvider.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import type { BoardCardActions_view$key } from '@atlassian/jira-relay/src/__generated__/BoardCardActions_view.graphql';
import type { BoardCardActions_project$key } from '@atlassian/jira-relay/src/__generated__/BoardCardActions_project.graphql';
import { ISSUE_TYPE_ID } from '../../../../../common/constants.tsx';
import type { BoardIssue } from '../../../../../common/types.tsx';
import type { DialogId } from './card-actions-menu/CardActionsMenuContent.tsx';
import { cardActionsMenuContentEntryPoint } from './card-actions-menu/entrypoint.tsx';
import messages from './messages.tsx';

export const CARD_ACTIONS_OPACITY_VARIABLE = '--card-actions-opacity';

export type Props = {
	issue: BoardIssue;
	columnFragment: BoardCardActions_column$key;
	projectFragment: BoardCardActions_project$key;
	viewFragment: BoardCardActions_view$key;
};

const styles = cssMap({
	fallbackMenu: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '220px',
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

const BoardCardActions = ({ issue, columnFragment, projectFragment, viewFragment }: Props) => {
	const triggerButtonRef = useRef<HTMLButtonElement>(null);
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();

	const view = useFragment(
		graphql`
			fragment BoardCardActions_view on JiraBoardView {
				id @required(action: THROW)
			}
		`,
		viewFragment,
	);

	const project = useFragment(
		graphql`
			fragment BoardCardActions_project on JiraProject {
				id @required(action: THROW)
			}
		`,
		projectFragment,
	);

	const column = useFragment(
		graphql`
			fragment BoardCardActions_column on JiraBoardViewColumn {
				__typename
				... on JiraBoardViewStatusColumn {
					statuses @required(action: THROW) {
						name @required(action: THROW)
					}
				}
				... on JiraBoardViewPriorityColumn {
					priority @required(action: THROW) {
						name @required(action: THROW)
					}
				}
				... on JiraBoardViewAssigneeColumn {
					user {
						name @required(action: THROW)
					}
				}
				... on JiraBoardViewCategoryColumn {
					category {
						value @required(action: THROW)
					}
				}
			}
		`,
		columnFragment,
	);

	let columnName;
	switch (column.__typename) {
		case 'JiraBoardViewAssigneeColumn':
			columnName = column.user?.name ?? formatMessage(messages.noAssignee);
			break;
		case 'JiraBoardViewPriorityColumn':
			columnName = column.priority.name;
			break;
		case 'JiraBoardViewCategoryColumn':
			columnName = column.category?.value ?? formatMessage(messages.noCategory);
			break;
		case 'JiraBoardViewStatusColumn':
			if (!column.statuses?.[0]) {
				throw new Error('Missing status column fields');
			}
			columnName = column.statuses[0].name;
			break;
		default:
			throw new Error(`Unhandled column type: ${column.__typename}`);
	}

	const [openDialogId, setOpenDialogId] = useState<DialogId>(null);

	const [isOpen, setIsOpen] = useState(false);
	const openMenu = useCallback(() => setIsOpen(true), []);
	const closeMenu = useCallback(() => {
		setIsOpen(false);
		if (fg('wanjel-a11y-enterprise-grade')) {
			setOpenDialogId(null);
			triggerButtonRef.current?.focus();
		}
	}, []);
	const toggleMenu = useCallback(() => setIsOpen((open) => !open), []);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		cardActionsMenuContentEntryPoint,
		useMemo(
			() => ({ cloudId, projectAri: project.id, viewId: view.id }),
			[cloudId, project.id, view.id],
		),
	);
	const menuContentTrigger = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const popupContent = useCallback(
		({ update }: { update: () => void }) => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="business-board-card-actions-menu"
				packageName="@atlassian/jira-work-management-board"
				runtimeProps={{
					issue,
					onContentResize: update,
					onClosePopup: closeMenu,
					onMenuRendered: update,
					onOpenPopup: openMenu,
					openDialogId,
					setOpenDialogId,
					shouldReturnFocus: fg('wanjel-a11y-enterprise-grade') ? triggerButtonRef : undefined,
				}}
				fallback={
					<Box xcss={styles.fallbackMenu}>
						<Spinner />
					</Box>
				}
			/>
		),
		[closeMenu, entryPointReferenceSubject, issue, openMenu, openDialogId, setOpenDialogId],
	);

	const workType = issue.fields[ISSUE_TYPE_ID].issueType.name;

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			const label = (() => {
				if (workType && workType.length && expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
					return formatMessage(messages.cardActionsWithIssueNameIssueTerminologyRefreshM4, {
						workType,
						columnName,
						issueKey: issue.fields.issuekey.value,
					});
				}
				return formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.cardActionsWithIssueNameIssueTermRefresh
						: messages.cardActionsWithIssueName,
					{
						columnName,
						issueKey: issue.fields.issuekey.value,
					},
				);
			})();

			return (
				<Box xcss={[IconButtonStyles, isOpen && IconButtonMenuOpenStyles]}>
					<IconButton
						{...triggerProps}
						ref={(element: HTMLButtonElement) => {
							menuContentTrigger(element);
							if (typeof triggerProps.ref === 'function') {
								if (fg('wanjel-a11y-enterprise-grade')) {
									mergeRefs(triggerProps.ref, triggerButtonRef)(element);
								} else {
									triggerProps.ref(element);
								}
							}
						}}
						icon={ShowMoreHorizontalIcon}
						label={label}
						appearance="subtle"
						onClick={toggleMenu}
						isSelected={isOpen}
						isTooltipDisabled={false}
						tooltip={{
							content: formatMessage(messages.moreActionsTooltip),
							hideTooltipOnClick: true,
						}}
						testId="work-management-board.ui.board.column.card.card-actions.trigger"
					/>
				</Box>
			);
		},
		[
			isOpen,
			toggleMenu,
			formatMessage,
			workType,
			columnName,
			issue.fields.issuekey.value,
			menuContentTrigger,
		],
	);

	let popUpRole = 'menu';
	if (openDialogId === 'confluence-pages' || openDialogId === 'issue-links') {
		popUpRole = 'dialog';
	}

	let popupTitleId;
	if (openDialogId === 'confluence-pages') {
		popupTitleId = 'business-confluence-pages.ui.add-link.heading';
	} else if (openDialogId === 'issue-links') {
		popupTitleId = 'link-issue-popup-title';
	}

	return (
		<ModalContextProvider>
			<Popup
				isOpen={isOpen}
				onClose={closeMenu}
				content={popupContent}
				placement="right-start"
				messageId="work-management-board.ui.board.column.card.card-actions.popup"
				messageType="transactional"
				trigger={trigger}
				role={fg('wanjel-a11y-enterprise-grade') ? popUpRole : undefined}
				titleId={popupTitleId}
				autoFocus={!fg('wanjel-a11y-enterprise-grade')} // recommended according to accessibility assessment, more info: https://hello.jira.atlassian.cloud/browse/A11Y-16161
			/>
		</ModalContextProvider>
	);
};

export default memo(BoardCardActions);

const IconButtonStyles = xcss({
	opacity: `var(${CARD_ACTIONS_OPACITY_VARIABLE}, 0)`,
	// These styles allow elements to receive pointer events even with the use of the
	// InteractionLayer component in the board card component. The InteractionLayer
	// component is used to create a clickable area for the card, but we want to
	// ensure that certain elements within the card can still receive pointer events.
	position: 'relative',
	pointerEvents: 'auto',
});

const IconButtonMenuOpenStyles = xcss({
	opacity: 1,
});
